'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { Menu, Bell, ChevronDown, Plus, Check } from 'lucide-react';
import AppImage from './ui/AppImage';
import { createClient } from '@/lib/supabase/client';
import { getUnreadNotificationCountDB, getNotificationsDB, markNotificationsReadDB } from '@/lib/supabaseData';
import type { Notification } from '@/lib/supabaseData';

interface TopbarProps {
  onMenuToggle: () => void;
  sidebarCollapsed: boolean;
}

export default function Topbar({ onMenuToggle }: TopbarProps) {
  const router = useRouter();
  const supabase = createClient();
  const [userName, setUserName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [userInitials, setUserInitials] = useState('IS');
  const [userId, setUserId] = useState<string | null>(null);
  const [notifCount, setNotifCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const loadNotifications = useCallback(async (uid: string) => {
    const [count, notifs] = await Promise.all([
      getUnreadNotificationCountDB(uid),
      getNotificationsDB(uid),
    ]);
    setNotifCount(count);
    setNotifications(notifs);
  }, []);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session?.user) {
        const uid = session.user.id;
        setUserId(uid);
        const { data } = await supabase
          .from('user_profiles')
          .select('full_name, role')
          .eq('id', uid)
          .single();
        if (data?.full_name) {
          setUserName(data.full_name);
          setUserRole(data.role || 'Staff');
          const parts = data.full_name.trim().split(' ');
          const initials =
            parts.length >= 2
              ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
              : data.full_name.slice(0, 2).toUpperCase();
          setUserInitials(initials);
        }
        await loadNotifications(uid);
      }
    });
  }, []);

  const handleBellClick = async () => {
    setNotifOpen((prev) => !prev);
    if (!notifOpen && userId && notifCount > 0) {
      await markNotificationsReadDB(userId);
      setNotifCount(0);
    }
  };

  const handleNewEntry = () => {
    router.push('/hutang-management');
  };

  const getNotifColor = (title: string) => {
    if (title.includes('Disetujui') || title.includes('Approved')) return 'bg-accent/10 border-l-4 border-accent';
    if (title.includes('Ditolak') || title.includes('Rejected')) return 'bg-danger/5 border-l-4 border-danger';
    return 'bg-secondary/30 border-l-4 border-warning';
  };

  return (
    <header className="h-16 bg-white border-b border-gray-100 flex items-center justify-between px-5 lg:px-7 flex-shrink-0 z-40 gap-4">
      {/* Left: Mobile menu */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={onMenuToggle}
          className="lg:hidden p-2 rounded-lg text-gray-500 hover:bg-gray-100 transition-all duration-150 flex-shrink-0"
          aria-label="Toggle menu"
        >
          <Menu size={20} />
        </button>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
        {/* Date Range Selector */}
        <button className="hidden md:flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50 transition-all duration-150">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <span className="font-500">Periode Ini</span>
          <ChevronDown size={14} className="text-gray-400" />
        </button>

        {/* Notification Bell */}
        <div className="relative">
          <button
            onClick={handleBellClick}
            className="relative p-2.5 rounded-xl bg-gray-50 text-gray-500 hover:bg-gray-100 transition-all duration-150"
          >
            <Bell size={18} />
            {notifCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-xs font-700 rounded-full flex items-center justify-center px-1 leading-none">
                {notifCount > 99 ? '99+' : notifCount}
              </span>
            )}
          </button>

          {/* Notification Dropdown */}
          {notifOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
              <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                  <h3 className="text-sm font-700 text-gray-800">Notifikasi</h3>
                  {notifications.length > 0 && (
                    <span className="text-xs text-gray-400">{notifications.length} notifikasi</span>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 px-4">
                      <Check size={24} className="text-gray-300 mb-2" />
                      <p className="text-sm text-gray-400">Tidak ada notifikasi</p>
                    </div>
                  ) : (
                    notifications.map((n) => (
                      <div
                        key={n.id}
                        className={`px-4 py-3 ${getNotifColor(n.title)} ${!n.isRead ? 'opacity-100' : 'opacity-60'}`}
                      >
                        <p className="text-xs font-700 text-gray-800 mb-0.5">{n.title}</p>
                        <p className="text-xs text-gray-600 leading-relaxed">{n.message}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(n.createdAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* User Info */}
        <div className="flex items-center gap-2.5 pl-2 border-l border-gray-100">
          <div className="w-9 h-9 rounded-full bg-[#1a3a6b] flex items-center justify-center text-white text-xs font-700 flex-shrink-0 overflow-hidden">
            <AppImage
              src="/assets/images/app_logo.png"
              alt={userInitials}
              width={36}
              height={36}
              className="object-cover w-full h-full"
              onError={() => {}}
            />
          </div>
          <div className="hidden sm:flex flex-col">
            <span className="text-sm font-600 text-gray-800 leading-tight">{userName || 'Admin'}</span>
            <span className="text-xs text-gray-400 leading-tight">{userRole || 'Administrator'}</span>
          </div>
          <ChevronDown size={14} className="text-gray-400 hidden sm:block" />
        </div>

        {/* New Entry Button */}
        <button
          onClick={handleNewEntry}
          className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-600 transition-all duration-150 hover:opacity-90 flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #1a3a6b 0%, #2563eb 100%)' }}
        >
          <Plus size={16} />
          <span>Pengajuan Baru</span>
        </button>
      </div>
    </header>
  );
}